import React, { useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import DutchieMenu from './components/DutchieMenu.tsx';
import Cart from './components/Cart.tsx';
import Footer from './Footer.tsx';
import CtComplianceBanner from './CtComplianceBanner.tsx';
import analyticsService from './services/AnalyticsService.ts';
import { STORE_MENU_CONFIG, MENU_ID_TO_STORE } from './config/storeMenuConfig.ts';


export default function MenuPage() {
  const { menuId, state, storeName } = useParams<{ menuId?: string; state?: string; storeName?: string }>();
  const location = useLocation();

  // Generate SEO-friendly meta data based on store and menu type
  const metaData = useMemo(() => {
    if (state && storeName) {
      const isNewUrlStructure = location.pathname.includes('dispensary-');
      const isMedical = location.pathname.includes('dispensary-medical-menu');
      const menuType = isMedical ? 'Medical' : 'Recreational';
      
      // Format state and store names for display
      const formattedState = state.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      const formattedStore = storeName.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      if (isNewUrlStructure) {
        return {
          title: `${menuType} Cannabis Menu | ${formattedStore}, ${formattedState} | Sweetspot Dispensary`,
          description: `Browse our ${menuType.toLowerCase()} cannabis menu at Sweetspot Dispensary in ${formattedStore}, ${formattedState}. Premium cannabis products, edibles, and more.`,
          canonical: `https://sweetspotfarms.com${location.pathname}`
        };
      }
    }
    
    // Fallback for old URL structure or missing params
    return {
      title: 'Cannabis Menu | Sweetspot Dispensary',
      description: 'Browse our premium cannabis menu featuring flower, edibles, concentrates, and more at Sweetspot Dispensary.',
      canonical: `https://sweetspotfarms.com${location.pathname}`
    };
  }, [state, storeName, location.pathname]);
  
  // Determine store configuration and menu details
  const menuConfig = useMemo(() => {
    // Priority 1: New URL structure with state/store/menu-type
    if (state && storeName) {
      const stateKey = state.toLowerCase();
      const storeKey = storeName.toLowerCase();
      const storeConfigKey = `${stateKey}/${storeKey}`;
      
      // Parse menu type from URL path
      const isMedical = location.pathname.includes('dispensary-medical-menu');
      const isRecreational = location.pathname.includes('dispensary-recreational-menu');
      const menuType = isMedical ? 'medical' : isRecreational ? 'recreational' : null;
      
      if (menuType && STORE_MENU_CONFIG[storeConfigKey]) {
        const storeConfig = STORE_MENU_CONFIG[storeConfigKey][menuType];
        if (storeConfig) {
          return {
            menuId: storeConfig.menuId, // Dutchie retailer ID
            legacyMenuId: storeConfig.legacyMenuId, // Legacy menu ID for deals
            menuType,
            storeLocation: `${stateKey}/${storeKey}`,
            source: 'new-url'
          };
        }
      }
    }
    
    // Priority 2: Legacy URL structure (/menu/:menuId)
    if (menuId) {
      const legacyMapping = MENU_ID_TO_STORE[menuId];
      
      if (legacyMapping) {
        const storeConfigKey = `${legacyMapping.state}/${legacyMapping.store}`;
        const storeConfig = STORE_MENU_CONFIG[storeConfigKey]?.[legacyMapping.type];

        if (storeConfig) {
          return {
            menuId: storeConfig.menuId, // Dutchie retailer ID
            legacyMenuId: storeConfig.legacyMenuId, // Legacy menu ID for deals
            menuType: legacyMapping.type,
            storeLocation: storeConfigKey,
            source: 'legacy-url'
          };
        }
      }

      // Fallback for unknown menu IDs (shouldn't happen with our stores)
      return {
        menuId,
        legacyMenuId: menuId,
        menuType: 'unknown',
        storeLocation: 'unknown',
        source: 'legacy-url-fallback'
      };
    }

    return null;
  }, [menuId, state, storeName, location.pathname]);

  // Track MenuPage renders
  const renderCount = useRef(0);
  renderCount.current += 1;

  // Extract values from config
  const actualMenuId = menuConfig?.menuId; // Dutchie retailer ID
  const legacyMenuId = menuConfig?.legacyMenuId; // Legacy menu ID for deals
  const menuType = menuConfig?.menuType;
  const storeLocation = menuConfig?.storeLocation;

  useEffect(() => {
    if (!menuConfig || !actualMenuId) return;

    // Store legacy menu ID in localStorage for purchase tracking
    localStorage.setItem('last_menu_accessed', legacyMenuId || actualMenuId || '');

    // Track menu access
    analyticsService.trackEvent('menu_interaction', {
      interaction_type: 'menu_access',
      menu_id: legacyMenuId || actualMenuId || 'unknown',
      menu_type: menuType || 'unknown',
      store_location: storeLocation || 'unknown',
      page_path: window.location.pathname,
      url_source: menuConfig.source
    });

    // Set user property for menu usage
    analyticsService.setUserProperties({
      last_menu_accessed: legacyMenuId || actualMenuId || 'unknown',
      last_menu_type: menuType || 'unknown',
      menu_user: 'true'
    }, 'behavioral');
  }, [menuConfig, actualMenuId, legacyMenuId, menuType, storeLocation, location.pathname]);

  if (!menuConfig || !actualMenuId) return null;

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'clip',
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: menuConfig.storeLocation.startsWith('connecticut') ? '35px' : undefined
    }}>
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={metaData.canonical} />
      </Helmet>
      <main style={{
        position: 'relative',
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: '2rem',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'clip'
      }}>
        {/* All stores now use DutchieMenu */}
        <DutchieMenu
          pricingType={menuType === 'medical' ? 'MEDICAL' : 'RECREATIONAL'}
          menuId={actualMenuId}
          legacyMenuId={legacyMenuId}
        />
      </main>
      
      {/* Cart Sidebar */}
      <Cart />
      
      <Footer />
      {menuConfig.storeLocation.startsWith('connecticut') && <CtComplianceBanner />}
    </div>
  );
}